import { handlePlural } from '@app/utils/handlePlural';

import {
	PersonalizationEnum,
	CreationMethod,
} from '@app/containers/CampaignCreationV2/constants';

import {
	DisplayMode,
	PersonalizationEnum as PersonalizationEnumV2,
} from '../../../containers/CampaignCreationV2/constants';

import { OptionsType } from '@app/types';
import {
	formatDateMonthNominalDay,
	formatDateMonthNominalDayAndTime,
	formatTimeAMPM,
} from '@app/utils/formatDate';
import { CampaignType } from '@app/redux/campaign/types';
import { AudienceSyncStatusEnum } from '@app/constants/modules/audience';
import {
	AudienceHandlingTypeEnum,
	DACampaignFeatureIdEnum,
	DOWN_FUNNEL_DA_ENROLLMENT_STEP_FEATURE_IDS,
	DeliveryChannelEnum,
	DuplicateCampaignConfirmationModalText,
	WebRecorderURLs,
	type DeliveryChannel,
	DOWN_FUNNEL_ENROLLMENT_STEP_FEATURE_IDS,
	AdvanceCampaignsRequirementIdsEnum,
} from '@app/constants/modules/campaign';
import { env } from '@app/env';
import { getFunnelIdByCampaignId } from '../funnels';
import { WorkspacePlansEnum } from '@app/constants/modules/workspace';
import {
	WorkspaceFeatureRequirement,
	WorkspaceType,
} from '@app/redux/workspace/types';
import { getWorkspaceFeature } from '@app/utils/modules/workspace';
import { PageRouteEnum } from '@app/constants/pages';
import { FunnelType } from '@app/components/modules/funnel/types';

export function checkIsCollectVideos(creationMethod: string): boolean {
	return (
		creationMethod === CreationMethod.collectVideos ||
		creationMethod === PersonalizationEnum.collectVideosSkipTemplates
	);
}

export function checkIsCollectVideosOpenSubmissions(
	campaign: Partial<CampaignType>,
): boolean {
	return (
		campaign.creationMethod === CreationMethod.collectVideos &&
		campaign.personalization === PersonalizationEnumV2.openSubmissions
	);
}

export function checkIsOpenSubmissions(
	campaign: Partial<CampaignType>,
): boolean {
	return (
		campaign.creationMethod === CreationMethod.openSubmissions ||
		campaign.personalization === PersonalizationEnumV2.openSubmissions
	);
}

export function checkIsOneToOne(
	campaign?: Partial<CampaignType>,
	creationMethod?: string,
	personalization?: string,
) {
	const isCollectVideos = checkIsCollectVideos(
		campaign?.creationMethod || creationMethod || '',
	);

	if (isCollectVideos) return false;

	return (
		campaign?.personalization === PersonalizationEnum.oneToOne ||
		personalization === PersonalizationEnum.oneToOne
	);
}

export const checkIsOneToMany = (personalization: string): boolean => {
	return personalization === PersonalizationEnum.oneToAll;
};

export const checkIsOneToWorld = (personalization: string): boolean => {
	return personalization === PersonalizationEnum.oneToWorld;
};

export function checkIsOneToManyAssignNewVideo(campaign: CampaignType) {
	return (
		campaign?.personalization === PersonalizationEnum.oneToAll &&
		campaign?.creationMethod === CreationMethod.createToDos
	);
}

export function checkIsOneToManyVideoFromLibrary(
	campaign: Partial<CampaignType>,
) {
	return (
		campaign?.personalization === PersonalizationEnum.oneToAll &&
		campaign?.creationMethod === CreationMethod.videoFromLibrary
	);
}

export function checkIsOneToWorldVideoFromLibrary(
	campaign: Partial<CampaignType>,
) {
	return (
		campaign?.personalization === PersonalizationEnum.oneToWorld &&
		campaign?.creationMethod === CreationMethod.videoFromLibrary
	);
}

export function checkIsCollectVideosPersonalization(
	personalization: string,
): boolean {
	return (
		personalization === PersonalizationEnum.collectVideos ||
		personalization === PersonalizationEnum.collectVideosSkipTemplates
	);
}

export function checkIsReels(personalization: string): boolean {
	return personalization === PersonalizationEnumV2.reels;
}

export function checkIsEmbedReels(campaign: CampaignType): boolean {
	const personalization = campaign?.personalization;
	const displayMode = campaign?.displayMode;

	return (
		personalization === PersonalizationEnumV2.reels &&
		displayMode === DisplayMode.EMBEDDED
	);
}

export function checkIsReelsOnlyAssignments(campaign: CampaignType): boolean {
	const isReels = campaign.personalization === PersonalizationEnumV2.reels;
	const hasNoCompletedStories =
		!campaign?.completedStories || !campaign?.completedStories?.length;

	const hasAssignedStories =
		campaign?.assignedStories && campaign?.assignedStories?.length;

	return isReels && hasNoCompletedStories && hasAssignedStories ? true : false;
}

export function checkIsReelsOnlyVFL(campaign: CampaignType): boolean {
	const isReels = campaign.personalization === PersonalizationEnumV2.reels;
	const hasNoAssignedStories =
		!campaign?.assignedStories || !campaign?.assignedStories?.length;

	const hasCompletedStories =
		campaign?.completedStories && campaign?.completedStories?.length;

	return isReels && hasNoAssignedStories && hasCompletedStories ? true : false;
}

export function checkIsReelsWithAssignments(campaign: CampaignType): boolean {
	const isReels = campaign.personalization === PersonalizationEnumV2.reels;
	const hasAssignedStories =
		campaign?.assignedStories && campaign?.assignedStories?.length;

	return isReels && hasAssignedStories ? true : false;
}

export function checkIsReelsWithVFL(campaign: CampaignType): boolean {
	const isReels = campaign.personalization === PersonalizationEnumV2.reels;
	const hasCompletedStories =
		campaign?.completedStories && campaign?.completedStories?.length;

	return isReels && hasCompletedStories ? true : false;
}

export function checkIsSMS(personalization: string): boolean {
	return personalization === PersonalizationEnum.SMS;
}

export function checkIsWhatsapp(
	whatsappEnabled?: boolean,
	whatsappTemplateId?: string,
	personalization?: string,
): boolean {
	if (
		whatsappEnabled &&
		whatsappTemplateId &&
		personalization === PersonalizationEnum.SMS
	) {
		return true;
	}

	return false;
}

export function checkIsNoVideoMilestonesCampaign(
	campaign: CampaignType,
): boolean {
	return (
		campaign.personalization === PersonalizationEnumV2.milestones &&
		campaign.creationMethod === CreationMethod.noVideo
	);
}

export function checkIsAdvanceCampaign(
	campaign: Partial<CampaignType>,
): boolean {
	return (
		campaign.nicheCorePlan === WorkspacePlansEnum.ADVANCE_1_5 ||
		campaign.nicheCorePlan === WorkspacePlansEnum.ADVANCE_2_0 ||
		campaign.nicheCorePlan === WorkspacePlansEnum.ADVANCE
	);
}

export function checkIsDACampaign(campaign: Partial<CampaignType>): boolean {
	return campaign.nicheCorePlan === WorkspacePlansEnum.DIRECT_ADMISSIONS;
}

/**
 * A DA enrollment-step campaign is an Enroll or Commit campaign within a
 * Direct Admissions funnel, identified by its `featureId`.
 */
export function checkIsDADownFunnelEnrollmentStepCampaign(
	campaign: Partial<CampaignType>,
): boolean {
	return (
		checkIsDACampaign(campaign) &&
		DOWN_FUNNEL_DA_ENROLLMENT_STEP_FEATURE_IDS.includes(
			campaign.featureId ?? '',
		)
	);
}

export function checkIsDownFunnelEnrollmentStepCampaign(
	campaign: Partial<CampaignType>,
): boolean {
	return DOWN_FUNNEL_ENROLLMENT_STEP_FEATURE_IDS.includes(
		campaign.featureId ?? '',
	);
}

/**
 * A top-of-funnel DA campaign is a Direct Admissions campaign that is not one
 * of the Enroll/Commit enrollment steps.
 */
export function checkIsTopFunnelDACampaign(
	campaign: Partial<CampaignType>,
): boolean {
	return (
		checkIsDACampaign(campaign) &&
		!checkIsDADownFunnelEnrollmentStepCampaign(campaign)
	);
}

const DA_EARLY_FUNNEL_STATUS_LABEL: Partial<
	Record<DACampaignFeatureIdEnum, string>
> = {
	[DACampaignFeatureIdEnum.ACCEPT]: 'Prospective',
	[DACampaignFeatureIdEnum.CONFIRM]: 'Conditional',
	[DACampaignFeatureIdEnum.ONBOARDED]: 'Onboarded',
};

/**
 * DA prospect/conditional/onboarded campaigns (Accept/Confirm/Onboarded
 * featureIds) don't have real CTAs yet, so the message preview replaces its
 * CTAs and enrollment-step copy with a placeholder for these three. Returns
 * the status word for that placeholder, or undefined for every other
 * featureId (including the DA Enroll/Commit enrollment steps).
 */
export function getDAEarlyFunnelStatusLabel(
	featureId: string | undefined,
): string | undefined {
	return DA_EARLY_FUNNEL_STATUS_LABEL[featureId as DACampaignFeatureIdEnum];
}

export function getCampaignCrmRequirementIds(
	campaign: Partial<CampaignType>,
): string[] {
	if (checkIsDADownFunnelEnrollmentStepCampaign(campaign)) {
		return [
			AdvanceCampaignsRequirementIdsEnum.ADVANCE_CRM_DATA_ON_TRACK,
			AdvanceCampaignsRequirementIdsEnum.ADVANCE_CRM_DATA_ON_TRACK_DA_SUPPLEMENTAL,
		];
	}

	if (checkIsDownFunnelEnrollmentStepCampaign(campaign)) {
		return [AdvanceCampaignsRequirementIdsEnum.ADVANCE_CRM_DATA_ON_TRACK];
	}

	return [];
}

export function checkIsCrmOnlyBlockingSend(
	campaign: Partial<CampaignType>,
	notSatisfiedRequirements: WorkspaceFeatureRequirement[],
): boolean {
	if (!notSatisfiedRequirements.length) return false;

	const crmRequirementIds = getCampaignCrmRequirementIds(campaign);
	if (!crmRequirementIds.length) return false;

	return notSatisfiedRequirements.every((requirement) =>
		crmRequirementIds.includes(requirement.id),
	);
}

export function checkIsLegacy1dot0Campaign(
	campaign: Partial<CampaignType>,
): boolean {
	return campaign.nicheCorePlan === WorkspacePlansEnum.ADVANCE_1_0;
}

type DeliveryChannelsInput =
	| Pick<CampaignType, 'deliveryChannel' | 'deliveryChannels'>
	| null
	| undefined;

/**
 * Returns the normalized list of delivery channels for a campaign.
 *
 * Prefers the new `deliveryChannels` array field when present; otherwise
 * derives it from the legacy `deliveryChannel` enum
 *   - 'all'         -> ['sms', 'email']
 *   - 'sms'         -> ['sms']
 *   - 'email'       -> ['email']
 *   - 'niche_inbox' -> ['niche_inbox']
 *
 * A campaign with neither field set resolves to `null`, which
 * consumers should treat as "no explicit preference" (e.g. fall back to
 * contact-preference-driven defaults).
 */
export function resolveDeliveryChannels(
	campaign: DeliveryChannelsInput,
): DeliveryChannel[] | null {
	if (campaign?.deliveryChannels?.length) {
		return campaign.deliveryChannels;
	}

	switch (campaign?.deliveryChannel) {
		case 'all':
			return ['sms', 'email'];
		case 'sms':
			return ['sms'];
		case 'email':
			return ['email'];
		case 'niche_inbox':
			return ['niche_inbox'];
		default:
			return null;
	}
}

export const hasEmailChannel = (campaign: DeliveryChannelsInput): boolean =>
	resolveDeliveryChannels(campaign)?.includes(DeliveryChannelEnum.EMAIL) ??
	false;

export const hasSmsChannel = (campaign: DeliveryChannelsInput): boolean =>
	resolveDeliveryChannels(campaign)?.includes(DeliveryChannelEnum.SMS) ?? false;

export const hasNicheInboxChannel = (
	campaign: DeliveryChannelsInput,
): boolean =>
	resolveDeliveryChannels(campaign)?.includes(
		DeliveryChannelEnum.NICHE_INBOX,
	) ?? false;

/**
 * True when the campaign is configured for exactly one delivery channel and
 * that channel matches `channel`.
 */
export const hasOnlyChannel = (
	campaign: DeliveryChannelsInput,
	channel: DeliveryChannel,
): boolean => {
	const channels = resolveDeliveryChannels(campaign);
	return channels?.length === 1 && channels[0] === channel;
};

export const deliveryChannelsMetadata = (campaign: DeliveryChannelsInput) => {
	const channels = resolveDeliveryChannels(campaign);
	return {
		hasNoExplicitChannels: !channels,
		includesEmail: channels?.includes(DeliveryChannelEnum.EMAIL) ?? false,
		includesSms: channels?.includes(DeliveryChannelEnum.SMS) ?? false,
		includesNicheInbox:
			channels?.includes(DeliveryChannelEnum.NICHE_INBOX) ?? false,
		isNicheInboxOnly:
			(channels?.length === 1 &&
				channels[0] === DeliveryChannelEnum.NICHE_INBOX) ??
			false,
	};
};

export const getEmailTemplateDropdownOptions = (
	templates: any,
): OptionsType[] => {
	const dropdownOptions = [];

	if (templates) {
		const emailTemplateOptions = templates.map((template: any) => ({
			label: template.name,
			value: template.id,
		}));

		dropdownOptions.push(...emailTemplateOptions);
	}

	return dropdownOptions;
};

// Text telling which emails are verified or not,
// that are going to be senders of the campaign
export const getSendingEmailText = ({
	defaultSendingAddress,
	storytellers,
	verifiedStorytellers,
	loading,
}: {
	defaultSendingAddress: string;
	storytellers: any[];
	verifiedStorytellers: {
		firstName: string;
		lastName: string;
		sendingAddress: string;
	}[];
	loading: boolean;
}) => {
	if (loading) return 'Loading...';

	const verifiedStorytellersAmount = verifiedStorytellers?.length || 0;

	if (verifiedStorytellersAmount === 0) return defaultSendingAddress;

	const selectedStorytellersAmount = storytellers?.length || 0;

	const hasAllEmailsVerified =
		!!verifiedStorytellersAmount &&
		!!selectedStorytellersAmount &&
		verifiedStorytellersAmount === selectedStorytellersAmount;

	const verifiedSenderText = `${verifiedStorytellersAmount} verified ${handlePlural(
		'sender',
		verifiedStorytellersAmount,
	)}`;

	if (hasAllEmailsVerified) return verifiedSenderText;

	const notVerifiedAmount =
		selectedStorytellersAmount - verifiedStorytellersAmount;

	const notVerifiedSenderText = `${notVerifiedAmount} ${handlePlural(
		'sender',
		notVerifiedAmount,
	)} with default address`;

	if (notVerifiedAmount && verifiedStorytellersAmount)
		return `${notVerifiedSenderText}, ${verifiedSenderText}`;

	return defaultSendingAddress;
};

export function getCampaignScheduledDate({
	scheduleDate,
	timezone,
	hideTime,
}: {
	scheduleDate: any;
	timezone?: string;
	hideTime?: boolean;
}): string {
	if (!scheduleDate) return '';

	if (timezone) {
		const dateFormatted = formatDateMonthNominalDayAndTime(
			scheduleDate,
			timezone,
		);

		const dateText = (dateFormatted as string).replace(', ', ' at ');

		if (hideTime) {
			return dateText.split(' at ')[0];
		}
		return dateText;
	}

	const date = `${formatDateMonthNominalDay(scheduleDate)}`.slice(0, -2);
	const time = hideTime ? '' : `at ${formatTimeAMPM(scheduleDate)}`;

	return `${date} ${time}`;
}

export function getCampaignExpirationDate({
	expirationDate,
	timezone,
}: {
	expirationDate: any;
	timezone?: string;
}): string {
	if (!expirationDate) return 'Add expiration date';
	const timezoneText = timezone ? ` (${timezone.replace('_', ' ')}).` : '';
	return `${getCampaignScheduledDate({
		scheduleDate: expirationDate,
		timezone: timezone,
	})}${timezoneText}`;
}

export function checkIsCampaignSyncingContacts(campaign: CampaignType) {
	if (!campaign?._id) return false;

	const hasContacts = campaign.contacts?.length > 0;

	const hasSynced = !!campaign.lastSyncDate;

	const audienceIsSyncing =
		campaign?.audience?.syncStatus === AudienceSyncStatusEnum.INACTIVE;

	return !hasSynced && !hasContacts && !audienceIsSyncing;
}

export function checkIsCampaignAudienceSyncInactive(campaign: CampaignType) {
	if (!campaign?._id) return false;
	const hasContacts = campaign.contacts?.length > 0;

	const hasSynced = !!campaign.lastSyncDate;

	const audienceIsSyncing =
		campaign?.audience?.syncStatus === AudienceSyncStatusEnum.INACTIVE;

	return !hasContacts && audienceIsSyncing && !hasSynced;
}

export function getAudienceHandlingText(audienceHandlingType: string) {
	switch (audienceHandlingType) {
		case AudienceHandlingTypeEnum.ONE_TIME_SYNC:
			return 'One time sync';
		case AudienceHandlingTypeEnum.ADDITIVE:
			return 'Additive';
		case AudienceHandlingTypeEnum.MAPPED:
			return 'Synced';
		default:
			return null;
	}
}

export function getRecurringCampaignText(campaign: CampaignType) {
	const isOneToOne = checkIsOneToOne(campaign);
	const audienceHandlingType = campaign?.audienceHandlingType;
	switch (audienceHandlingType) {
		case AudienceHandlingTypeEnum.ONE_TIME_SYNC:
			return 'Off';
		case AudienceHandlingTypeEnum.ADDITIVE:
			if (isOneToOne) return 'On • Additive';
			return 'On';
		case AudienceHandlingTypeEnum.MAPPED:
			return 'On • Synced';
		default:
			return '';
	}
}

export function getDuplicateCampaignDescription(campaign: CampaignType) {
	const isSmsCampaign = checkIsSMS(campaign?.personalization);
	const isEmbedReelsCampaign = checkIsEmbedReels(campaign);
	const isCollectVideosCampaign = checkIsCollectVideos(
		campaign?.creationMethod,
	);

	switch (true) {
		case isSmsCampaign:
			return DuplicateCampaignConfirmationModalText.DESCRIPTION_INBOX;
		case isEmbedReelsCampaign:
		case isCollectVideosCampaign:
			return DuplicateCampaignConfirmationModalText.DESCRIPTION_NO_SEND;
		default:
			return DuplicateCampaignConfirmationModalText.DESCRIPTION;
	}
}

export function getWebRecorderURL() {
	const currentEnv = env.NODE_ENV || 'local';

	const urlMap = {
		production: WebRecorderURLs.PROD,
		prod: WebRecorderURLs.PROD,
		qa: WebRecorderURLs.QA,
		development: WebRecorderURLs.DEV,
		dev: WebRecorderURLs.DEV,
	};

	// Return the matched environment URL or default to LOCAL
	return urlMap[currentEnv as keyof typeof urlMap] || WebRecorderURLs.LOCAL;
}

export function getCampaignOrFunnelRedirectURL(
	campaign: CampaignType,
	funnels?: FunnelType[],
) {
	const isAdvanceCampaign =
		checkIsAdvanceCampaign(campaign) || checkIsDACampaign(campaign);

	const defaultRedirectURL = `/${PageRouteEnum.campaign}/${campaign._id}`;

	if (isAdvanceCampaign) {
		const funnelId = funnels
			? getFunnelIdByCampaignId(campaign._id, funnels)
			: undefined;

		if (!funnelId) {
			return defaultRedirectURL;
		}

		return `/${PageRouteEnum.funnel}/${funnelId}`;
	}

	return defaultRedirectURL;
}

export function checkAdvanceCampaignAllowedToSend(
	campaign: CampaignType,
	workspace: WorkspaceType,
): { allowed: boolean; sendFeatureId: string } {
	const campaignFeatureId = campaign.featureId;
	const sendFeature = campaignFeatureId?.replace('enable', 'send');

	const campaignFeatureStatus = getWorkspaceFeature(
		workspace,
		sendFeature ?? '',
	);

	return {
		allowed: campaignFeatureStatus?.allowed ?? false,
		sendFeatureId: sendFeature ?? '',
	} as const;
}

export function checkAdvanceCampaignSendStatus(
	campaign: CampaignType,
	workspace: WorkspaceType,
): {
	sending: boolean;
	notSatisfiedRequirements: WorkspaceFeatureRequirement[];
	crmOnly: boolean;
} {
	const { allowed, sendFeatureId } = checkAdvanceCampaignAllowedToSend(
		campaign,
		workspace,
	);

	if (allowed) {
		return {
			sending: true,
			notSatisfiedRequirements: [],
			crmOnly: false,
		} as const;
	} else {
		const campaignFeatureStatus = getWorkspaceFeature(workspace, sendFeatureId);

		const notSatisfiedRequirements =
			campaignFeatureStatus?.requirementGroups?.reduce((acc, group) => {
				return [
					...acc,
					...group.requirements.filter((requirement) => !requirement.satisfied),
				];
			}, [] as WorkspaceFeatureRequirement[]) ?? [];

		return {
			sending: false,
			notSatisfiedRequirements,
			crmOnly: checkIsCrmOnlyBlockingSend(campaign, notSatisfiedRequirements),
		} as const;
	}
}
